import { useCallback, useEffect, useState } from 'react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { OrgInstance, OrgMember } from '@/types/orgTypes';
import { createReadSequence } from '@/utils/readSequence';

export type MembershipStatus = 'loading' | 'ready' | 'error';

// Only the latest read or local update lands, so a slow older reply never wins
export function useAccountMembership(userId: string | undefined) {
  const services = useServices();
  const [orgMember, setOrgMember] = useState<OrgMember | null>(null);
  const [orgInstance, setOrgInstance] = useState<OrgInstance | null>(null);
  const [status, setStatus] = useState<MembershipStatus>('loading');
  const [reads] = useState(createReadSequence);

  // Another user starts from nothing
  const [loadedFor, setLoadedFor] = useState<{ userId?: string } | null>(null);
  if (!loadedFor || loadedFor.userId !== userId) {
    setLoadedFor({ userId });
    setOrgMember(null);
    setOrgInstance(null);
    setStatus(userId ? 'loading' : 'ready');
  }

  const load = useCallback((quiet: boolean) => {
    const mine = reads.next();
    if (!userId) return Promise.resolve();
    // The membership and its org in one round trip (getMyOrg needs no read of the membership first)
    return Promise.all([services.organization.fetchOrgMember(userId), services.organization.fetchMyOrgInstance()])
      .then(([member, instance]) => {
        if (!member) throw new Error('No membership found');
        if (!instance) throw new Error('No organization found');
        return { member, instance };
      })
      .then(({ member, instance }) => {
        if (!reads.isLatest(mine)) return;
        reads.markKnown();
        setOrgMember(member);
        setOrgInstance(instance);
        setStatus('ready');
      })
      .catch(error => {
        if (!reads.isLatest(mine)) return;
        console.error('Error loading org data:', error);
        // A background refresh that fails keeps what the page already shows
        if (!quiet || !reads.isKnown()) setStatus('error');
      });
  }, [userId, services.organization, reads]);

  useEffect(() => {
    reads.reset();
    void load(false);
    return () => reads.reset();
  }, [load, reads]);

  const updateMember = useCallback((change: (member: OrgMember) => OrgMember) => {
    reads.next();
    setOrgMember(member => (member ? change(member) : member));
  }, [reads]);

  const updateInstance = useCallback((change: (instance: OrgInstance) => OrgInstance) => {
    reads.next();
    setOrgInstance(instance => (instance ? change(instance) : instance));
  }, [reads]);

  const retry = useCallback(() => {
    setStatus('loading');
    void load(false);
  }, [load]);

  const refresh = useCallback(() => {
    if (!reads.isKnown()) setStatus('loading');
    void load(true);
  }, [load, reads]);

  return { orgMember, orgInstance, status, retry, refresh, updateMember, updateInstance };
}
